import React, { PureComponent } from "react"
import classNames from "classnames"
import { Mutation } from "react-apollo"

import Cta from "../cta/cta"

import {
  GENERATE_FOUNDER_ASSIST_INITIAL,
  UPDATE_FOUNDER_ASSIST_PARAGRAPHS,
  MY_FOUNDER_ASSIST_QUERY,
} from "../../lib/founder-assist-queries"
import { hasMinimumQuestionnaire } from "../founder-questionnaire/founder-questionnaire"
import { GenerationWatcher, graphQLErrorText } from "../../lib/founder-assist-jobs"

import "./founder-plan-document.scss"

const DAY_MS = 24 * 60 * 60 * 1000
const LOADING_CAPTIONS = [
  "Reading your latest answers…",
  "Drafting an outlook from what we heard…",
  "Thinking about what to do next…",
]
const PRIORITY_RANK = { high: 0, medium: 1, low: 2, unprioritized: 3 }
const PRIORITY_LABEL = {
  high: "High",
  medium: "Medium",
  low: "Low",
  unprioritized: "Unprioritized",
}

function relativeDrafted(iso) {
  if (!iso) return "just now"
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS))
  if (days === 0) return "today"
  if (days === 1) return "yesterday"
  if (days < 14) return `${days} days ago`
  if (days < 56) return `${Math.round(days / 7)} weeks ago`
  return `${Math.round(days / 30)} months ago`
}

function exactDate(iso) {
  if (!iso) return ""
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch (_) {
    return ""
  }
}

function pickThisQuarterFocus(actions) {
  if (!Array.isArray(actions)) return []
  return actions
    .filter((a) => a && a.status === "open")
    .sort((a, b) => {
      const pa = PRIORITY_RANK[a.priority || "unprioritized"]
      const pb = PRIORITY_RANK[b.priority || "unprioritized"]
      if (pa !== pb) return pa - pb
      const ia = Number.isFinite(a.impactScore) ? a.impactScore : 0
      const ib = Number.isFinite(b.impactScore) ? b.impactScore : 0
      if (ib !== ia) return ib - ia
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
    .slice(0, 3)
}

class FounderPlanDocument extends PureComponent {
  constructor(props) {
    super(props)
    const fa = props.fa || {}
    this.state = {
      summary: fa.summaryParagraph || "",
      plan: fa.sixMonthPlanParagraph || "",
      editingSummary: false,
      editingPlan: false,
      menuOpen: false,
      showRegenConfirm: false,
      generating: false,
      captionIdx: 0,
      errorMessage: "",
      savedSummary: false,
      savedPlan: false,
    }
    this._captionTimer = null
    this._summaryRef = React.createRef()
    this._planRef = React.createRef()
    this._menuRef = React.createRef()
    this._generation = new GenerationWatcher((errorText) => {
      this.stopCaptions()
      this.safeSetState({ generating: false, errorMessage: errorText || "" })
    })
  }

  componentDidMount() {
    this._mounted = true
    document.addEventListener("mousedown", this.handleClickOutside)
  }

  componentWillUnmount() {
    this._mounted = false
    document.removeEventListener("mousedown", this.handleClickOutside)
    if (this._captionTimer) clearInterval(this._captionTimer)
    if (this._savedSummaryTimer) clearTimeout(this._savedSummaryTimer)
    if (this._savedPlanTimer) clearTimeout(this._savedPlanTimer)
    this._generation.stop()
  }

  safeSetState(state) {
    if (this._mounted) this.setState(state)
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.fa || {}
    const fa = this.props.fa || {}
    if (
      !this.state.editingSummary &&
      prev.summaryParagraph !== fa.summaryParagraph
    ) {
      this.setState({ summary: fa.summaryParagraph || "" })
    }
    if (
      !this.state.editingPlan &&
      prev.sixMonthPlanParagraph !== fa.sixMonthPlanParagraph
    ) {
      this.setState({ plan: fa.sixMonthPlanParagraph || "" })
    }
    this._generation.update(fa)
  }

  handleClickOutside = (e) => {
    if (
      this._menuRef.current &&
      !this._menuRef.current.contains(e.target) &&
      this.state.menuOpen
    ) {
      this.setState({ menuOpen: false })
    }
  }

  startEditSummary = () => {
    this.setState({ editingSummary: true }, () => {
      if (this._summaryRef.current) {
        this._summaryRef.current.focus()
        this._summaryRef.current.setSelectionRange(
          this.state.summary.length,
          this.state.summary.length
        )
      }
    })
  }

  startEditPlan = () => {
    this.setState({ editingPlan: true }, () => {
      if (this._planRef.current) {
        this._planRef.current.focus()
        this._planRef.current.setSelectionRange(
          this.state.plan.length,
          this.state.plan.length
        )
      }
    })
  }

  cancelEditSummary = () => {
    this.setState({
      editingSummary: false,
      summary: (this.props.fa && this.props.fa.summaryParagraph) || "",
    })
  }

  cancelEditPlan = () => {
    this.setState({
      editingPlan: false,
      plan: (this.props.fa && this.props.fa.sixMonthPlanParagraph) || "",
    })
  }

  saveSummary = (saveParas) => {
    const original =
      (this.props.fa && this.props.fa.summaryParagraph) || ""
    if (this.state.summary === original) {
      this.setState({ editingSummary: false })
      return
    }
    saveParas({ variables: { summary: this.state.summary } }).then(() => {
      this.safeSetState({ editingSummary: false, savedSummary: true })
      if (this._savedSummaryTimer) clearTimeout(this._savedSummaryTimer)
      this._savedSummaryTimer = setTimeout(
        () => this.safeSetState({ savedSummary: false }),
        1500,
      )
    })
  }

  savePlan = (saveParas) => {
    const original =
      (this.props.fa && this.props.fa.sixMonthPlanParagraph) || ""
    if (this.state.plan === original) {
      this.setState({ editingPlan: false })
      return
    }
    saveParas({ variables: { plan: this.state.plan } }).then(() => {
      this.safeSetState({ editingPlan: false, savedPlan: true })
      if (this._savedPlanTimer) clearTimeout(this._savedPlanTimer)
      this._savedPlanTimer = setTimeout(
        () => this.safeSetState({ savedPlan: false }),
        1500,
      )
    })
  }

  startCaptions() {
    this._captionTimer = setInterval(() => {
      this.setState((p) => ({
        captionIdx: (p.captionIdx + 1) % LOADING_CAPTIONS.length,
      }))
    }, 4000)
  }

  stopCaptions() {
    if (this._captionTimer) clearInterval(this._captionTimer)
    this._captionTimer = null
  }

  handleGenerate = (generate) => {
    this.setState({
      generating: true,
      menuOpen: false,
      showRegenConfirm: false,
      errorMessage: "",
      captionIdx: 0,
    })
    this.startCaptions()
    // The mutation only starts the job; the watcher finishes when the polled
    // fa shows generationInProgress back to false.
    this._generation.start()
    generate()
      .then(() => this._generation.update(this.props.fa))
      .catch((e) => {
        this._generation.stop()
        this.stopCaptions()
        this.safeSetState({
          generating: false,
          errorMessage: graphQLErrorText(
            e,
            "We had trouble drafting your plan. Try again, or simplify one of your answers slightly."
          ),
        })
      })
  }

  scrollToActions = () => {
    if (typeof document === "undefined") return
    const el = document.getElementById("actions")
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  renderImpact(score) {
    const n = Number.isFinite(score) ? score : 0
    return (
      <span
        className="founder-plan-document__impact"
        aria-label={`Impact ${n}/5`}
      >
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className={classNames("founder-plan-document__impact-dot", {
              "founder-plan-document__impact-dot--on": i <= n,
            })}
          />
        ))}
      </span>
    )
  }

  renderEmptyOrLoading(meetsMinimum, hasQuestionnaire, generate, loading) {
    const { captionIdx, errorMessage } = this.state
    const fa = this.props.fa
    const generating =
      this.state.generating || Boolean(fa && fa.generationInProgress)

    if (generating || loading) {
      return (
        <div className="founder-plan-document founder-plan-document--skeleton">
          <span className="founder-plan-document__eyebrow">
            Your 6-month outlook
          </span>
          <div className="founder-plan-document__skel founder-plan-document__skel--lg" />
          <div className="founder-plan-document__skel" />
          <div className="founder-plan-document__skel founder-plan-document__skel--lg" />
          <p
            className="founder-plan-document__skel-caption"
            aria-live="polite"
          >
            {LOADING_CAPTIONS[captionIdx]}
          </p>
        </div>
      )
    }

    return (
      <div className="founder-plan-document founder-plan-document--empty">
        <span className="founder-plan-document__eyebrow">
          Your 6-month outlook
        </span>
        <h2 className="founder-plan-document__empty-headline">
          Your plan is ready to be drafted.
        </h2>
        <p className="founder-plan-document__empty-sub">
          Save your questionnaire, then we'll draft a personalized summary, a
          6-month plan, and your first set of actions in about 30 seconds.
        </p>
        {errorMessage && (
          <div className="founder-plan-document__error" role="alert">
            <strong>We couldn't generate your plan.</strong>
            <p>{errorMessage}</p>
          </div>
        )}
        <div className="founder-plan-document__empty-cta">
          <Cta
            tag="button"
            type="button"
            text="Save & generate my plan"
            disabled={!hasQuestionnaire || !meetsMinimum}
            onClick={() => this.handleGenerate(generate)}
          />
        </div>
      </div>
    )
  }

  render() {
    const { fa } = this.props
    const {
      summary,
      plan,
      editingSummary,
      editingPlan,
      menuOpen,
      showRegenConfirm,
      savedSummary,
      savedPlan,
      errorMessage,
      generating,
    } = this.state

    const hasPlan = Boolean(
      fa && (fa.summaryParagraph || fa.sixMonthPlanParagraph)
    )
    const hasQuestionnaire = Boolean(fa && fa.questionnaireSavedAt)
    const meetsMinimum =
      fa && hasMinimumQuestionnaire(fa, fa.mode || "company")
    const focus = pickThisQuarterFocus(fa ? fa.actions : [])

    const draftedAt =
      fa &&
      (fa.summaryModifiedAt && fa.planModifiedAt
        ? new Date(fa.summaryModifiedAt) > new Date(fa.planModifiedAt)
          ? fa.summaryModifiedAt
          : fa.planModifiedAt
        : fa.summaryModifiedAt || fa.planModifiedAt || null)

    // generationInProgress also covers a reload mid-generation.
    if (!hasPlan || generating || (fa && fa.generationInProgress)) {
      return (
        <Mutation mutation={GENERATE_FOUNDER_ASSIST_INITIAL}>
          {(generate, { loading }) =>
            this.renderEmptyOrLoading(
              meetsMinimum,
              hasQuestionnaire,
              generate,
              loading
            )
          }
        </Mutation>
      )
    }

    return (
      <Mutation
        mutation={UPDATE_FOUNDER_ASSIST_PARAGRAPHS}
        refetchQueries={[{ query: MY_FOUNDER_ASSIST_QUERY }]}
      >
        {(saveParas) => (
          <article className="founder-plan-document">
            <header className="founder-plan-document__header">
              <span className="founder-plan-document__eyebrow">
                Your 6-month outlook
              </span>
              <div className="founder-plan-document__byline">
                <span title={exactDate(draftedAt)}>
                  Drafted {relativeDrafted(draftedAt)}
                </span>
                <div
                  className="founder-plan-document__menu-wrap"
                  ref={this._menuRef}
                >
                  <button
                    type="button"
                    className="founder-plan-document__menu-trigger"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={() =>
                      this.setState((p) => ({ menuOpen: !p.menuOpen }))
                    }
                    title="More"
                  >
                    <span aria-hidden>⋯</span>
                  </button>
                  {menuOpen && (
                    <Mutation mutation={GENERATE_FOUNDER_ASSIST_INITIAL}>
                      {(generate) => (
                        <div
                          className="founder-plan-document__menu"
                          role="menu"
                        >
                          <button
                            role="menuitem"
                            type="button"
                            onClick={() =>
                              this.setState({
                                menuOpen: false,
                                showRegenConfirm: true,
                              })
                            }
                          >
                            Redraft my outlook…
                          </button>
                          <button
                            role="menuitem"
                            type="button"
                            onClick={() => {
                              this.setState({ menuOpen: false })
                              this.startEditSummary()
                            }}
                          >
                            Edit outlook
                          </button>
                          <button
                            role="menuitem"
                            type="button"
                            onClick={() => {
                              this.setState({ menuOpen: false })
                              this.startEditPlan()
                            }}
                          >
                            Edit plan
                          </button>
                        </div>
                      )}
                    </Mutation>
                  )}
                </div>
              </div>
            </header>

            {/* SUMMARY paragraph */}
            {editingSummary ? (
              <div className="founder-plan-document__edit">
                <textarea
                  ref={this._summaryRef}
                  className="founder-plan-document__edit-input"
                  value={summary}
                  onChange={(e) =>
                    this.setState({ summary: e.target.value })
                  }
                  rows={6}
                />
                <div className="founder-plan-document__edit-actions">
                  <Cta
                    tag="button"
                    type="button"
                    text="Save"
                    onClick={() => this.saveSummary(saveParas)}
                  />
                  <Cta
                    tag="button"
                    type="button"
                    basic
                    text="Cancel"
                    onClick={this.cancelEditSummary}
                  />
                </div>
              </div>
            ) : (
              <p
                className="founder-plan-document__prose"
                onClick={this.startEditSummary}
                title="Click to edit"
              >
                {summary || (
                  <span className="founder-plan-document__prose-empty">
                    No outlook yet — redraft your outlook to write one.
                  </span>
                )}
                {savedSummary && (
                  <span className="founder-plan-document__saved-flash">
                    ✓ Saved
                  </span>
                )}
              </p>
            )}

            {/* THIS QUARTER focus block */}
            {focus.length > 0 && (
              <>
                <div className="founder-plan-document__divider">
                  <span className="founder-plan-document__divider-text">
                    This quarter, focus on
                  </span>
                </div>
                <ul className="founder-plan-document__focus">
                  {focus.map((a) => {
                    const priority = a.priority || "unprioritized"
                    return (
                      <li
                        key={a.id}
                        className={classNames(
                          "founder-plan-document__focus-item",
                          `founder-plan-document__focus-item--${priority}`
                        )}
                        onClick={this.scrollToActions}
                      >
                        <span
                          className={classNames(
                            "founder-plan-document__focus-chip",
                            `founder-plan-document__focus-chip--${priority}`
                          )}
                        >
                          {PRIORITY_LABEL[priority]}
                        </span>
                        {this.renderImpact(a.impactScore)}
                        <span className="founder-plan-document__focus-title">
                          {a.title}
                        </span>
                      </li>
                    )
                  })}
                </ul>
                <button
                  type="button"
                  className="founder-plan-document__focus-jump"
                  onClick={this.scrollToActions}
                >
                  View all open actions ↓
                </button>
              </>
            )}

            {/* PLAN paragraph */}
            {editingPlan ? (
              <div className="founder-plan-document__edit">
                <textarea
                  ref={this._planRef}
                  className="founder-plan-document__edit-input"
                  value={plan}
                  onChange={(e) => this.setState({ plan: e.target.value })}
                  rows={8}
                />
                <div className="founder-plan-document__edit-actions">
                  <Cta
                    tag="button"
                    type="button"
                    text="Save"
                    onClick={() => this.savePlan(saveParas)}
                  />
                  <Cta
                    tag="button"
                    type="button"
                    basic
                    text="Cancel"
                    onClick={this.cancelEditPlan}
                  />
                </div>
              </div>
            ) : plan ? (
              <p
                className="founder-plan-document__prose"
                onClick={this.startEditPlan}
                title="Click to edit"
              >
                {plan}
                {savedPlan && (
                  <span className="founder-plan-document__saved-flash">
                    ✓ Saved
                  </span>
                )}
              </p>
            ) : null}

            {/* Refresh confirm dialog */}
            {showRegenConfirm && (
              <Mutation mutation={GENERATE_FOUNDER_ASSIST_INITIAL}>
                {(generate, { loading }) => (
                  <div
                    className="founder-plan-document__confirm"
                    role="dialog"
                  >
                    <p>
                      This rewrites your outlook and 6-month plan from your
                      latest answers. Your open actions and completed history
                      stay. Redraft?
                    </p>
                    <div className="founder-plan-document__confirm-actions">
                      <Cta
                        tag="button"
                        type="button"
                        text="Redraft my outlook"
                        loading={loading}
                        disabled={loading}
                        onClick={() => this.handleGenerate(generate)}
                      />
                      <Cta
                        tag="button"
                        type="button"
                        basic
                        text="Cancel"
                        onClick={() =>
                          this.setState({ showRegenConfirm: false })
                        }
                      />
                    </div>
                  </div>
                )}
              </Mutation>
            )}

            {errorMessage && (
              <div className="founder-plan-document__error" role="alert">
                <strong>We couldn't redraft your outlook.</strong>
                <p>{errorMessage}</p>
              </div>
            )}
          </article>
        )}
      </Mutation>
    )
  }
}

export default FounderPlanDocument
